import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Globe, Calendar, MapPin, Shield, Zap, Wifi, WifiOff, Crown, Sparkles,
  ArrowRight, CheckCircle, Smartphone, Database, Lock, BarChart3,
  Layers, Cpu, Phone, MessageSquare, Star, QrCode, FileText, Download
} from 'lucide-react';

const PLATFORM_FEATURES = [
  {
    icon: Globe,
    title: 'Personal Doctor Website',
    desc: 'Every doctor gets a professional, standalone website at their own URL. Not a listing on a marketplace — their own digital property.',
    premium: false,
  },
  {
    icon: Calendar,
    title: 'Multi-Location, Multi-Service Scheduling',
    desc: 'Define any number of non-contiguous time blocks per day across locations. Each block can mix OPD, Consulting, Surgeries, and Minor Procedures — patients see only the slots for the service they\'re booking.',
    premium: false,
  },
  {
    icon: Layers,
    title: 'Slot-Level Control (Red / Amber / Green)',
    desc: 'Toggle individual 15-min slots from Available to Tentative to Blocked. Real-time reflection on patient side. Full doctor control.',
    premium: false,
  },
  {
    icon: Shield,
    title: 'Structured Cancellation Protocol',
    desc: '7 standard reason codes (Emergency Surgery, Clinic Overrun, Personal Emergency…). 1-hour buffer enforced server-side. Every cancel writes an audit log + patient email + push. No ghosting, ever.',
    premium: false,
  },
  {
    icon: Crown,
    title: 'Premium Procedure Catalog',
    desc: '42+ pre-written procedures across 6 specialties. Click to select, auto-populates on website. No writing needed.',
    premium: true,
  },
  {
    icon: Sparkles,
    title: '2 AI Pulls per Month (TurboCharge)',
    desc: 'TurboCharge doctors get 2 monthly AI pulls — spend them on SEO articles, per-procedure landing pages, AI case-study check, address parse, or smart procedure blending. Nothing auto-fires. Zero surprise token burn.',
    premium: true,
  },
  {
    icon: Download,
    title: 'Install-as-an-App (PWA)',
    desc: 'Patients see a discreet install prompt. Adds DocSite to their home screen. Maskable Android icon, branded splash, instant open, works offline — feels native without the app store.',
    premium: false,
  },
  {
    icon: Smartphone,
    title: 'Photo Upload + Auto-Optimization',
    desc: 'Upload from phone or laptop. Auto-compressed (60%+ reduction), EXIF stripped, fast-loading. No URL hassle.',
    premium: false,
  },
  {
    icon: BarChart3,
    title: 'Website Readiness Score',
    desc: 'Gamified 0-100 score showing profile completeness. Checklist guides doctors to build a high-converting website.',
    premium: false,
  },
  {
    icon: WifiOff,
    title: 'Works Offline',
    desc: 'Patient visits your website once — it works offline forever. Profile, procedures, schedule — all cached. Zero internet needed to browse.',
    premium: false,
  },
  {
    icon: QrCode,
    title: 'Clinic Kit (TurboCharge)',
    desc: 'Print-ready A4 QR poster + sticker pack for your clinic wall. Patients scan, your website opens — even offline after first visit. Secondary vCard QR saves your contact 100% offline.',
    premium: true,
  },
  {
    icon: FileText,
    title: 'AI SEO Articles You Own (TurboCharge)',
    desc: 'Two AI-drafted SEO articles per month. You edit, approve, publish with one click. Every edit audit-trailed — the content is yours forever.',
    premium: true,
  },
  {
    icon: QrCode,
    title: '"Save My Details" Widget (TurboCharge)',
    desc: 'A floating QR pill on your public website — patients tap once and get two QR codes: save your contact offline, or open your site on another phone. Perfect for in-clinic use.',
    premium: true,
  },
  {
    icon: Zap,
    title: 'Turbo Visibility Accelerator (TurboCharge)',
    desc: 'Guided checklist to tell Google you\'re real. 5 simple link placements across platforms — pre-written text, one-tap copy. Your visibility compounds every month.',
    premium: true,
  },
  {
    icon: MessageSquare,
    title: 'Patient Referral Engine',
    desc: 'After appointments, patients share your profile with tracked referral links. Every click and appointment is attributed. Your patients become your organic growth engine.',
    premium: false,
  },
  {
    icon: Database,
    title: 'Auto-SEO Landing Pages',
    desc: 'Your practice automatically appears on specialty+city search pages — "Cardiologists in Mumbai." New pages auto-create as doctors join. Zero effort, compounding visibility.',
    premium: false,
  },
  {
    icon: FileText,
    title: 'Medical Insights Hub',
    desc: 'Your published articles appear on a public knowledge hub alongside other specialists. Patients discover you through the expertise you share — not through ads.',
    premium: false,
  },
  {
    icon: Shield,
    title: 'RMP Compliance & Trust',
    desc: 'NMC/State Medical Council registration displayed on your profile. Patients see self-declared credentials — not paid rankings. Trust earned through transparency.',
    premium: false,
  },
];

const WHATS_LIVE = [
  'Doctor personal websites',
  'Multi-location, multi-service scheduling (OPD / Consulting / Surgeries / Minor Procedures filtered per block)',
  'Structured cancellation protocol — 7 reason codes, 1-hr buffer, audit log, patient email + push',
  'Install-as-an-App (PWA) with maskable icon + non-intrusive install prompt',
  '**Smart QR Hook** — patients scan doctor posters/links to auto-save clinic + redirect to booking',
  '**Zero-Touch Onboarding** — new patients register via a doctor\'s QR link and land with the clinic pre-saved',
  '**My Trusted Medical Clinics** patient dashboard with "Book again" and one-tap Google Maps directions',
  'Premium procedure catalog',
  'Offline browsing support',
  'Website readiness score with NMC registration tracking',
  'Photo upload with auto-optimization',
  'Clinic Kit — printable QR poster with offline-ready patient flow (TurboCharge)',
  '"Save My Details" on-site QR widget — live on every TurboCharge doctor page',
  '2 AI pulls per month — spend across SEO articles, procedure pages, AI check, address parse, or blending (TurboCharge)',
  'AI SEO articles — doctor-owned, one-click publish (TurboCharge)',
  'Per-procedure SEO landing pages (TurboCharge)',
  'Turbo Visibility Accelerator — guided 5 Links Challenge (TurboCharge)',
  'Patient Referral Engine — tracked referral links with click attribution',
  'Medical Insights Hub — public article aggregation for organic discovery',
  'Auto-SEO specialty + city landing pages — zero-effort platform visibility',
  'Internal cross-linking — procedures, articles, and profiles auto-interlinked',
  'Clinic display name + address + OpenStreetMap embed (Premium)',
  'Per-procedure price ranges shown publicly (Premium)',
  'Post-appointment chat — 3-message coordination between doctor and patient',
  'Platform Feedback system with admin inbox',
];

const DIFFERENTIATORS = [
  { title: 'Not a Marketplace', desc: 'Aggregator platforms list doctors alongside competitors, ranked by ad spend. DocSite gives each doctor their OWN website. No competition on the same page. No pay-to-appear.' },
  { title: 'Doctor Owns the Patient', desc: 'No platform intermediation. Patient books directly on the doctor\'s website. Doctor\'s brand, doctor\'s patient relationship. No commercial exploitation.' },
  { title: 'Skills Speak, Not Ad Spend', desc: 'Your visibility is earned through real medical expertise — published articles, detailed procedures, patient referrals. The more you know, the higher you rank. Democratized visibility.' },
  { title: 'Works Without Internet', desc: 'Offline browsing via PWA — designed for India\'s real connectivity landscape. Works in Tier 2/3 cities. A doctor in a small town has the same tools as one in a metro hospital.' },
  { title: 'Clinical Depth, Not Just Listing', desc: 'Detailed procedures, NMC registration, AI-generated articles, cross-linked expertise — not just a name and phone number.' },
  { title: 'Your Data is Yours', desc: 'Your patient data is never sold, shared, or used for advertising. Period. You own your practice data completely.' },
];

export default function BrochurePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Hero */}
      <section className="bg-gradient-to-br from-sky-50 via-white to-slate-50 py-20 md:py-28" data-testid="brochure-hero">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sky-100 border border-sky-200 text-xs font-medium text-sky-700 mb-6">
            <Star size={12} /> Platform Overview
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-slate-900 leading-[1.1]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            DocSite
            <span className="block font-medium text-sky-600">The Doctor's Digital OS</span>
          </h1>
          <p className="text-lg text-slate-600 mt-6 max-w-2xl mx-auto leading-relaxed">
            A SaaS platform that gives every doctor their own professional website with intelligent scheduling, premium content automation, and offline-first architecture — built for India's healthcare reality.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            <button onClick={() => navigate('/register')} className="rounded-full px-8 py-3.5 bg-sky-600 text-white font-medium hover:bg-sky-700 transition-colors flex items-center gap-2" data-testid="brochure-get-started">
              Get Started <ArrowRight size={16} />
            </button>
            <button onClick={() => navigate('/dr/dr-rajesh-sharma')} className="rounded-full px-8 py-3.5 bg-white text-sky-700 font-medium hover:bg-sky-50 transition-colors border border-sky-200">
              View Live Demo
            </button>
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section className="py-20 md:py-28" data-testid="brochure-features">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-sky-600 mb-3">What We've Built</p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Platform Capabilities
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {PLATFORM_FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className={`rounded-2xl p-6 border ${f.premium ? 'border-amber-200 bg-gradient-to-br from-amber-50/50 to-orange-50/30' : 'border-slate-200 bg-white'} hover:shadow-md transition-shadow`} data-testid={`brochure-feature-${i}`}>
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${f.premium ? 'bg-amber-100' : 'bg-sky-50'}`}>
                      <Icon size={20} className={f.premium ? 'text-amber-600' : 'text-sky-600'} strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{f.title}</h3>
                        {f.premium && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[9px] font-bold border border-amber-200">PREMIUM</span>}
                      </div>
                      <p className="text-sm text-slate-600 mt-1 leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="brochure-differentiators">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-sky-600 mb-3">Why DocSite</p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              What Makes Us Different
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {DIFFERENTIATORS.map((d, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6" data-testid={`diff-${i}`}>
                <div className="w-8 h-8 rounded-lg bg-sky-600 flex items-center justify-center mb-3">
                  <span className="text-white font-bold text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{i + 1}</span>
                </div>
                <h4 className="font-medium text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{d.title}</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Honest Ratings */}
      <section className="py-16 md:py-20 bg-amber-50/50 border-y border-amber-100" data-testid="brochure-ratings">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 mb-4">
              {[1,2,3,4,5].map(n => <Star key={n} size={24} className="text-amber-400" fill="currentColor" />)}
            </div>
            <h3 className="text-2xl font-medium text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Ratings That Are Earned, Not Bought
            </h3>
            <p className="text-base text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Doctor ratings on DocSite are generated automatically from patient feedback on actual clinical outcomes — not frivolous rating systems based on perceived experiences that are often manipulated. 
              DocSite does not and will not sell rating improvement programs or promote doctors in any way. 
              Doctors deserve real insights to improve. Patients deserve a system they can trust.
            </p>
          </div>
        </div>
      </section>

      {/* Offline Architecture */}
      <section className="py-20 md:py-28" data-testid="brochure-offline">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-sky-600 mb-3">Built for India</p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Offline-First Architecture
            </h2>
            <p className="text-base text-slate-600 mt-3 max-w-xl mx-auto">Not dependent on internet. Multiple fallback channels ensure patients can always reach doctors.</p>
          </div>
          <div className="space-y-4">
            {[
              { icon: Wifi, label: 'Layer 1: Offline Browsing', desc: 'Doctor website cached on first visit. Works fully offline — profile, procedures, schedule all viewable without internet.', status: 'Live' },
              { icon: MessageSquare, label: 'Layer 2: SMS Booking', desc: 'Patient sends SMS to book. Works on any phone, any network. No smartphone needed.', status: 'Coming Soon' },
              { icon: Phone, label: 'Layer 3: Missed Call Booking', desc: 'Patient gives a missed call — system calls back with voice menu. Book appointments without internet or a smartphone. Rural-scale reach.', status: 'Coming Soon' },
              { icon: Database, label: 'Layer 4: Offline Dashboard', desc: 'Doctor sees today\'s bookings, schedules, locations and procedures even when the clinic Wi-Fi drops. Read-only cache; edits flush when online.', status: 'Live' },
            ].map((layer, i) => {
              const Icon = layer.icon;
              return (
                <div key={i} className="flex items-start gap-4 bg-white rounded-2xl border border-slate-200 p-5">
                  <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center flex-shrink-0">
                    <Icon size={20} className="text-sky-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-slate-900 text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{layer.label}</h4>
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${layer.status === 'Live' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-500 border border-slate-200'}`}>{layer.status}</span>
                    </div>
                    <p className="text-sm text-slate-600 mt-0.5">{layer.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What's Live */}
      <section className="py-20 md:py-28 bg-slate-50 border-t border-slate-200" data-testid="brochure-live">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-600 mb-3">Available Now</p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              What's Live Today
            </h2>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-2xl mx-auto shadow-sm">
            <div className="space-y-3">
              {WHATS_LIVE.map((item, j) => (
                <div key={j} className="flex items-center gap-3 text-base text-slate-700">
                  <CheckCircle size={18} className="text-emerald-500 flex-shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-500 mt-6 text-center">And we're shipping new features every week.</p>
          </div>
        </div>
      </section>

      {/* Data Privacy Trust Statement */}
      <section className="py-16 md:py-20 bg-white border-t border-slate-200" data-testid="brochure-privacy">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="bg-slate-50 rounded-3xl border border-slate-200 p-8 md:p-12 text-center">
            <div className="w-14 h-14 rounded-2xl bg-sky-50 flex items-center justify-center mx-auto mb-5">
              <Shield size={28} className="text-sky-600" strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-medium text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Your Data. Your Patients. Your Practice.
            </h3>
            <p className="text-base text-slate-600 leading-relaxed max-w-2xl mx-auto">
              We believe your patient data belongs to you — and only you. DocSite will <strong>never sell, share, or monetize</strong> your patient information. 
              We do not use your data for advertising, analytics resale, or third-party profiling. 
              Your practice data stays private, secure, and under your control at all times.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><Lock size={14} className="text-sky-500" /> Encrypted at rest</span>
              <span className="flex items-center gap-1.5"><Shield size={14} className="text-sky-500" /> Never sold to third parties</span>
              <span className="flex items-center gap-1.5"><Database size={14} className="text-sky-500" /> Doctor-owned data</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-sky-600" data-testid="brochure-cta">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-medium text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Ready to Build Your Digital Medical Practice?</h2>
          <button onClick={() => navigate('/register')} className="mt-6 rounded-full px-8 py-3.5 bg-white text-sky-700 font-medium hover:bg-sky-50 transition-colors inline-flex items-center gap-2">
            Get Started Free <ArrowRight size={16} />
          </button>
        </div>
      </section>
    </div>
  );
}
